import {
  AdyenHostedLink,
  AdyenKYCDetails,
  AdyenOnboardingRequest,
  OnboardingStartView
} from "../../Contracts/Onboarding";
import { useRequestStore } from "../BackendStore";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PaymentsIntegrationEnum, PaymentsIntegrationView } from "../../Contracts/Integration";
import { useInvoiceStore } from "../Invoice";

const getAdyenKYCDetailsUrl = "/v1/payments/get-kyc-details";
const getConnectedIntegrationUrl = "/v1/payments/integrations/connected";

const startOnboardingUrl = "/v1/payments/onboarding/start";
const submitOnboardingFormUrl = "/v1/payments/onboarding/submit";

export interface OnboardingState {
  isLoading: boolean;
  adyenKYCDetails?: AdyenKYCDetails;
  getAdyenKYCDetails: (getAccessTokenSilently: any) => Promise<void>;
  onboardingStartView: OnboardingStartView;
  startOnboarding: (getAccessTokenSilently: any) => Promise<void>;
  isOnboardingComplete: () => boolean;
  resetOnboarding: () => void;
  integrationReAuthRequired?: boolean;
  integrationReAuthMessage?: string;
  submitOnboardingForm: (
    getAccessTokenSilently: any,
    form: AdyenOnboardingRequest
  ) => Promise<void>;
  hostedLink?: string;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => {
      const { getRequest, postRequest } = useRequestStore.getState();
      const { setInvoiceBaseUrl } = useInvoiceStore.getState();

      const getAdyenKYCDetails = async (getAccessTokenSilently: any) => {
        set({ isLoading: true });
        const response = await getRequest<AdyenKYCDetails>(
          getAccessTokenSilently,
          getAdyenKYCDetailsUrl
        );
        let integrationResponse: PaymentsIntegrationView | undefined = undefined;
        if (
          response.connected_integration === PaymentsIntegrationEnum.Xero ||
          response.connected_integration === PaymentsIntegrationEnum.Quickbooks
        ) {
          integrationResponse = await getRequest<PaymentsIntegrationView>(
            getAccessTokenSilently,
            getConnectedIntegrationUrl
          );
        }
        if (!!response.connected_integration) {
          setInvoiceBaseUrl(
            response.connected_integration === PaymentsIntegrationEnum.Xero
              ? process.env.REACT_APP_XERO_INVOICE_URL
              : response.connected_integration === PaymentsIntegrationEnum.Quickbooks
                ? process.env.REACT_APP_QBO_INVOICE_URL
                : undefined
          );
        }
        set({
          adyenKYCDetails: response,
          isLoading: false,
          integrationReAuthRequired: integrationResponse?.re_authorization_required,
          integrationReAuthMessage: integrationResponse?.re_authorization_banner_message
        });
      };

      const resetOnboarding = () => {
        set({ adyenKYCDetails: undefined });
      };

      const startOnboarding = async (getAccessTokenSilently: any) => {
        set({ isLoading: true });
        const response = await getRequest<OnboardingStartView>(
          getAccessTokenSilently,
          startOnboardingUrl
        );
        set({
          onboardingStartView: response,
          hostedLink: response.hosted_link_url,
          isLoading: false
        });
      };

      const submitOnboardingForm = async (
        getAccessTokenSilently: any,
        form: AdyenOnboardingRequest
      ) => {
        const response = await postRequest<AdyenOnboardingRequest, AdyenHostedLink>(
          getAccessTokenSilently,
          submitOnboardingFormUrl,
          form
        );
        set({ hostedLink: response.url });
      };

      const isOnboardingComplete = () => {
        return (
          (get().adyenKYCDetails?.is_kyc_started || false) &&
          !!get().adyenKYCDetails?.connected_integration
        );
      };

      return {
        isLoading: true,
        onboardingStartView: {
          legal_entities: [],
          countries: []
        },
        getAdyenKYCDetails,
        isOnboardingComplete,
        resetOnboarding,
        startOnboarding,
        submitOnboardingForm,
        integrationReAuthRequired: undefined,
        integrationReAuthMessage: undefined
      };
    },
    {
      name: "onboarding-store",
      partialize: state => ({
        adyenKYCDetails: state.adyenKYCDetails,
        integrationReAuthRequired: state.integrationReAuthRequired,
        integrationReAuthMessage: state.integrationReAuthMessage
      })
    }
  )
);
